import React from "react";
import { cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import { useNavigate, useLocation } from "react-router-dom";

const buttonClasses = cva(
  "inline-flex items-center justify-center font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95",
  {
    variants: {
      variant: {
        primary:
          "bg-[transparent] text-white hover:bg-[#183076] focus:ring-[#183076] border border-[#183076]",
        secondary:
          "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500",
        outline: "border-2 bg-transparent hover:bg-gray-50 focus:ring-blue-500",
        ghost: "bg-transparent hover:bg-gray-100 text-gray-700",
      },
      size: {
        small: "text-[14px] px-3 py-1.5",
        medium: "text-[18px] px-4 py-3",
        large:
          "text-[16px] md:text-[18px] 2xl:text-[22px] px-4 md:px-6 py-2 md:py-4 font-bold",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "large",
    },
  },
);

const Button = ({
  // Required parameters with defaults
  text = "Book Now",
  text_font_size = "",
  text_font_family = "Bricolage Grotesque",
  text_font_weight = "font-bold",
  text_line_height = "",
  text_text_align = "center",
  text_color = "",
  fill_background_color = "",
  border_border_radius = "rounded-full",
  border_border = "",

  // Navigation prop
  to,
  activeClassName = "", // Class to apply when path matches 'to'

  // Optional parameters (no defaults)
  layout_width,
  padding,
  position,
  margin,

  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = "button",
  ...props
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Custom active state logic
  const isActive = to ? location.pathname === to : false;

  // Safe validation for optional parameters
  const hasValidWidth =
    layout_width &&
    typeof layout_width === "string" &&
    layout_width?.trim() !== "";
  const hasValidPadding =
    padding && typeof padding === "string" && padding?.trim() !== "";
  const hasValidMargin =
    margin && typeof margin === "string" && margin?.trim() !== "";
  const hasValidPosition =
    position && typeof position === "string" && position?.trim() !== "";

  // Parse padding from string format
  const getPaddingClasses = (paddingStr) => {
    if (!paddingStr) return "";
    const paddingParts = paddingStr?.split(",");
    let classes = [];

    paddingParts?.forEach((part) => {
      const [side, value] = part?.split("=");
      const cleanValue = value?.replace("px", "");
      if (side && cleanValue) {
        switch (side?.trim()) {
          case "t":
            classes?.push(`pt-[${cleanValue}px]`);
            break;
          case "r":
            classes?.push(`pr-[${cleanValue}px]`);
            break;
          case "b":
            classes?.push(`pb-[${cleanValue}px]`);
            break;
          case "l":
            classes?.push(`pl-[${cleanValue}px]`);
            break;
        }
      }
    });

    return classes?.join(" ");
  };

  // Parse margin from string format
  const getMarginClasses = (marginStr) => {
    if (!marginStr) return "";
    const marginParts = marginStr?.split(",");
    let classes = [];

    marginParts?.forEach((part) => {
      const [side, value] = part?.split("=");
      const cleanValue = value?.replace("px", "");
      if (side && cleanValue) {
        switch (side?.trim()) {
          case "t":
            classes?.push(`mt-[${cleanValue}px]`);
            break;
          case "r":
            classes?.push(`mr-[${cleanValue}px]`);
            break;
          case "b":
            classes?.push(`mb-[${cleanValue}px]`);
            break;
          case "l":
            classes?.push(`ml-[${cleanValue}px]`);
            break;
        }
      }
    });

    return classes?.join(" ");
  };

  // Build optional Tailwind classes
  const optionalClasses = [
    hasValidWidth
      ? layout_width.includes("[")
        ? layout_width
        : `w-${layout_width}`
      : "",
    hasValidPadding ? getPaddingClasses(padding) : "",
    hasValidMargin ? getMarginClasses(margin) : "",
    hasValidPosition ? position : "",
  ]
    ?.filter(Boolean)
    ?.join(" ");

  // Build required style classes
  const requiredClasses = [
    text_font_size,
    text_font_weight,
    text_line_height,
    text_color,
    fill_background_color,
    border_border_radius,
    text_text_align === "center" ? "text-center" : `text-${text_text_align}`,
  ]
    ?.filter(Boolean)
    ?.join(" ");

  // Safe click handler
  const handleClick = (event) => {
    if (disabled) return;
    if (to) {
      navigate(to);
    }
    if (typeof onClick === "function") {
      onClick(event);
    }
  };

  return (
    <button
      type={type}
      // disabled={disabled}
      onClick={handleClick}
      style={{
        fontFamily: text_font_family,
        ...(border_border && { border: border_border }),
      }}
      className={twMerge(
        buttonClasses({ variant, size }),
        requiredClasses,
        optionalClasses,
        "cursor-pointer",
        isActive && activeClassName, // Apply active classes if matches path
        className,
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </button>
  );
};

export default Button;
